import axios from 'axios';
import { API_URL } from '../config/apiConfig';

const activeRequests = new Set();
const objectUrls = new Set();
const invalidationListeners = new Set();
const allowedMediaTypes = new Set([
  'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'
]);

const abortError = () => new DOMException('הבקשה בוטלה', 'AbortError');
export const isMediaRequestCancelled = error => error?.name === 'AbortError';

// Blob URLs are private, short-lived leases. Never persist or log them.
export const revokeBookingMediaUrl = url => {
  if (url && objectUrls.delete(url)) window.URL.revokeObjectURL(url);
};

export const createBookingMediaUrl = (blob, token) => {
  if (!token || localStorage.getItem('token') !== token) throw abortError();
  const url = window.URL.createObjectURL(blob);
  objectUrls.add(url);
  return url;
};

export const subscribeBookingMediaInvalidation = listener => {
  invalidationListeners.add(listener);
  return () => invalidationListeners.delete(listener);
};

// Called synchronously when an authenticated session ends or changes.
export const invalidateBookingMedia = (message = null) => {
  activeRequests.forEach(controller => controller.abort());
  [...objectUrls].forEach(revokeBookingMediaUrl);
  invalidationListeners.forEach(listener => listener(message));
};

class BookingMediaError extends Error {}

const responseError = (status, fallback) => {
  const message = {
    400: 'בקשה לא תקינה',
    401: 'אין הרשאה - נדרשת התחברות מחדש',
    403: 'אין הרשאה לפעולה זו',
    404: 'הקובץ או ההזמנה לא נמצאו',
    409: 'רשימת הקבצים השתנתה. רענן את ההזמנה ונסה שוב',
    413: 'הקבצים גדולים מדי. נסה קבצים קטנים יותר'
  }[status] || fallback;
  return new BookingMediaError(message);
};

const attachmentPath = (bookingId, imageIndex, { expectedImageId, download } = {}) => {
  if (!bookingId || (imageIndex !== undefined && (!Number.isInteger(imageIndex) || imageIndex < 0))) {
    throw new Error('מזהה קובץ או הזמנה לא תקין');
  }
  let path = `/api/bookings/${encodeURIComponent(bookingId)}/images`;
  if (imageIndex !== undefined) path += `/${imageIndex}`;
  const params = new URLSearchParams();
  if (expectedImageId) params.set('expectedImageId', expectedImageId);
  if (download) params.set('download', 'true');
  if (params.toString()) path += `?${params}`;
  return `${(axios.defaults.baseURL || API_URL).replace(/\/$/, '')}${path}`;
};

// Use fetch rather than the app's logging Axios interceptors. Credentials are
// carried only in the Authorization header, never an <img> URL or query string.
const requestAttachment = async (path, options, consume, fallback) => {
  const token = localStorage.getItem('token');
  if (!token) throw responseError(401, fallback);
  const controller = new AbortController();
  const cancel = () => controller.abort();
  const { signal, timeout, ...requestOptions } = options;
  if (signal?.aborted) throw abortError();
  signal?.addEventListener('abort', cancel, { once: true });
  activeRequests.add(controller);
  const timer = timeout ? setTimeout(cancel, timeout) : null;
  try {
    const response = await fetch(path, {
      ...requestOptions,
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
      cache: 'no-store',
      redirect: 'error',
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    });
    if (!response.ok || response.redirected) {
      const failure = responseError(response.status, fallback);
      if (response.status === 401 || response.status === 403) invalidateBookingMedia(failure.message);
      throw failure;
    }
    const result = await consume(response);
    if (controller.signal.aborted || localStorage.getItem('token') !== token) throw abortError();
    return result;
  } catch (error) {
    // Never expose a request/response object, server text, URL or credentials.
    if (error instanceof BookingMediaError) throw new Error(error.message);
    if (controller.signal.aborted || isMediaRequestCancelled(error)) throw abortError();
    throw new Error(fallback);
  } finally {
    if (timer) clearTimeout(timer);
    signal?.removeEventListener('abort', cancel);
    activeRequests.delete(controller);
  }
};

const imageService = {
  uploadBookingImages: async (bookingId, files, options = {}) => {
    const formData = new FormData();
    Array.from(files).slice(0, 2).forEach(file => formData.append('images', file));
    return requestAttachment(
      attachmentPath(bookingId),
      { method: 'POST', body: formData, timeout: 60000, ...options },
      response => response.json(),
      'שגיאה בהעלאת התמונות'
    );
  },

  deleteBookingImage: (bookingId, imageIndex, options = {}) => requestAttachment(
    attachmentPath(bookingId, imageIndex, options),
    { method: 'DELETE', signal: options.signal },
    response => response.json(),
    'שגיאה במחיקת התמונה'
  ),

  fetchBookingImage: (bookingId, imageIndex, options = {}) => requestAttachment(
    attachmentPath(bookingId, imageIndex, options),
    { method: 'GET', signal: options.signal },
    async response => {
      const blob = await response.blob();
      if (!blob.size || !allowedMediaTypes.has(blob.type.toLowerCase())) {
        throw new Error('סוג קובץ לא נתמך או קובץ ריק');
      }
      return blob;
    },
    'שגיאה בטעינת הקובץ'
  ),

  downloadBookingImage: async (bookingId, imageIndex, filename = null, options = {}) => {
    const token = localStorage.getItem('token');
    const blob = await imageService.fetchBookingImage(bookingId, imageIndex, { ...options, download: true });
    if (options.signal?.aborted) throw abortError();
    const url = createBookingMediaUrl(blob, token);
    const link = document.createElement('a');
    try {
      link.href = url;
      link.download = filename || `booking-${bookingId}-image-${imageIndex + 1}`;
      document.body.appendChild(link);
      link.click();
    } finally {
      link.remove();
      // Keep the lease for one turn so the browser can start the download.
      // Logout still revokes it synchronously through invalidateBookingMedia.
      setTimeout(() => revokeBookingMediaUrl(url), 0);
    }
  },

  /**
   * בדיקת תקינות קובץ לפני העלאה
   * @param {File} file - הקובץ לבדיקה
   * @returns {Object} תוצאת הבדיקה
   */
  validateFile: (file) => {
    const result = {
      isValid: true,
      error: null
    };

    // בדיקת סוג קובץ
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'application/pdf'
    ];

    if (!allowedTypes.includes(file.type)) {
      result.isValid = false;
      result.error = 'סוג קובץ לא נתמך. יש להעלות JPG, PNG, GIF או PDF';
      return result;
    }

    // בדיקת גודל קובץ (5MB מקסימום)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      result.isValid = false;
      result.error = 'הקובץ גדול מדי. גודל מקסימלי: 5MB';
      return result;
    }

    return result;
  },

  /**
   * בדיקת תקינות מספר קבצים
   * @param {FileList|File[]} files - רשימת הקבצים
   * @param {number} currentImagesCount - מספר התמונות הקיימות
   * @returns {Object} תוצאת הבדיקה
   */
  validateFilesCount: (files, currentImagesCount = 0) => {
    const result = {
      isValid: true,
      error: null
    };

    const totalImages = currentImagesCount + files.length;

    if (totalImages > 2) {
      result.isValid = false;
      result.error = `ניתן להעלות מקסימום 2 תמונות. כרגע יש ${currentImagesCount} תמונות קיימות`;
      return result;
    }

    return result;
  },

  /**
   * פורמט גודל קובץ לתצוגה
   * @param {number} bytes - גודל בבתים
   * @returns {string} גודל מפורמט
   */
  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  /**
   * קבלת אייקון לפי סוג קובץ
   * @param {string} mimetype - סוג הקובץ
   * @returns {string} שם האייקון
   */
  getFileTypeIcon: (mimetype) => {
    if (mimetype.startsWith('image/')) {
      return 'image';
    } else if (mimetype === 'application/pdf') {
      return 'picture_as_pdf';
    }
    return 'insert_drive_file';
  }
};

export default imageService;
